import { useState, useEffect } from 'react'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { coursesAPI, getApiBaseUrlWithoutApi } from '../../config/api'
import 'primeicons/primeicons.css'

export default function CurriculumPage() {
  const { theme } = useTheme()
  const [curricula, setCurricula] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchCurricula = async () => {
      try {
        setLoading(true)
        const data = await coursesAPI.getCurriculumLessons()
        
        // Xử lý các định dạng response khác nhau
        let curriculaList: any[] = []
        if (Array.isArray(data)) {
          curriculaList = data
        } else if (data?.results && Array.isArray(data.results)) {
          curriculaList = data.results
        } else if (data?.data && Array.isArray(data.data)) {
          curriculaList = data.data
        }
        
        setCurricula(curriculaList)
      } catch (error) {
        setCurricula([])
      } finally {
        setLoading(false)
      }
    }

    fetchCurricula()
  }, [])

  const API_BASE_URL = getApiBaseUrlWithoutApi()

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
      <Header />

      {/* Header Section */}
      <section className={`py-16 md:py-20 px-4 ${theme === 'dark' ? 'bg-gradient-to-b from-black to-gold-900/10' : 'bg-gradient-to-b from-gray-200 to-gold-100/20'}`}>
        <div className="container mx-auto text-center max-w-4xl">
          <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-4 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-500'}`}>
            Thử Thách
          </h1>
          <p className={`text-lg md:text-xl ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
            4 video thử thách của tháng - Unlock mỗi tuần
          </p>
        </div>
      </section>

      {/* Curricula Section */}
      <section className={`py-12 md:py-16 px-4 ${theme === 'dark' ? 'bg-black' : 'bg-gray-200'}`}>
        <div className="container mx-auto max-w-7xl">
          {loading ? (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải thử thách...</p>
            </div>
          ) : curricula.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {curricula.map((curriculum) => {
                // Xử lý URL hình ảnh
                let imageUrl = curriculum.image
                if (imageUrl && !imageUrl.startsWith('http')) {
                  imageUrl = `${API_BASE_URL}${imageUrl}`
                }
                
                return (
                  <div
                    key={curriculum.id}
                    className={`rounded-xl overflow-hidden border ${theme === 'dark' ? 'border-gold-500/40 bg-black/60' : 'border-gold-400 bg-white'} shadow-lg ${theme === 'dark' ? 'shadow-gold-500/20' : 'shadow-gray-200'} hover:shadow-xl transition-all duration-300 group flex flex-col h-full`}
                  >
                    {/* Image or Icon */}
                    {imageUrl ? (
                      <div className="relative h-48 overflow-hidden flex-shrink-0">
                        <img
                          src={imageUrl}
                          alt={curriculum.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        />
                      </div>
                    ) : (
                      <div className={`p-6 pb-4 flex-shrink-0 ${theme === 'dark' ? 'bg-gold-500/10' : 'bg-gold-100/50'}`}>
                        <div className={`w-16 h-16 rounded-full bg-gold-gradient flex items-center justify-center shadow-lg ${theme === 'dark' ? 'shadow-gold-500/30' : 'shadow-gold-400/40'}`}>
                          <i className="pi pi-file-edit text-2xl text-black"></i>
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="p-6 flex flex-col flex-grow">
                      {/* Title */}
                      <h3 className={`text-xl md:text-2xl font-bold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {curriculum.title}
                      </h3>

                      {/* Description */}
                      <p className={`text-sm md:text-base leading-relaxed mb-4 flex-grow ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                        {curriculum.description}
                      </p>

                      {/* Course Title (if available) */}
                      {curriculum.course_title && (
                        <div className="flex items-center gap-2 mb-2">
                          <i className={`pi pi-book text-gold-400 text-sm`}></i>
                          <span className={`text-xs font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {curriculum.course_title}
                          </span>
                        </div>
                      )}

                      {/* Lessons Count and Duration */}
                      <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gold-500/20 flex-shrink-0">
                        <div className="flex items-center gap-2">
                          <i className={`pi pi-calendar text-gold-400`}></i>
                          <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            {curriculum.lesson_count || 0} buổi học
                          </span>
                        </div>
                        {curriculum.duration_days && (
                          <div className="flex items-center gap-2">
                            <i className={`pi pi-clock text-gold-400`}></i>
                            <span className={`text-sm font-semibold ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                              {curriculum.duration_days} ngày
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Coming Soon Button */}
                      <button
                        disabled
                        className={`w-full px-6 py-3 rounded-lg font-bold transition flex items-center justify-center gap-2 flex-shrink-0 cursor-not-allowed ${
                          theme === 'dark'
                            ? 'bg-gray-700 text-gray-400 border border-gray-600'
                            : 'bg-gray-300 text-gray-600 border border-gray-400'
                        }`}
                      >
                        <i className="pi pi-clock"></i>
                        <span>Sắp ra mắt</span>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <div className={`text-center py-16 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              <i className="pi pi-file-edit text-6xl mb-4 text-gold-400"></i>
              <p className="text-lg">Chưa có thử thách nào</p>
            </div>
          )}
        </div>
      </section>
    </main>
  )
}
